import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export type MediaMosaicItem = {
  key: string;
  src: string;
  alt?: string;
  kind: "image" | "video";
  href?: string;
};

export function MediaMosaic({
  items,
  overlay,
  className,
}: {
  items: MediaMosaicItem[];
  overlay?: (item: MediaMosaicItem, index: number) => ReactNode;
  className?: string;
}) {
  const count = items.length;
  if (count === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg",
        count === 1 && "block",
        count === 2 && "grid grid-cols-2 gap-1",
        count === 3 && "grid h-[22rem] grid-cols-2 grid-rows-2 gap-1",
        count >= 4 && "grid grid-cols-2 gap-1",
        className,
      )}
    >
      {items.map((item, index) => (
        <MosaicTile
          key={item.key}
          item={item}
          count={count}
          index={index}
          overlay={overlay?.(item, index)}
        />
      ))}
    </div>
  );
}

function MosaicTile({
  item,
  count,
  index,
  overlay,
}: {
  item: MediaMosaicItem;
  count: number;
  index: number;
  overlay?: ReactNode;
}) {
  const mediaClassName =
    count === 1 ? "max-h-96 w-full object-cover" : "h-full w-full object-cover";
  const media =
    item.kind === "video" ? (
      <video src={item.src} controls className={cn(mediaClassName, "bg-black")} />
    ) : (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={item.src}
        alt={item.alt ?? "Csatolt kép"}
        className={mediaClassName}
      />
    );

  return (
    <div
      className={cn(
        "relative min-h-0 overflow-hidden bg-muted",
        count === 1 && "max-h-96",
        count === 2 && "aspect-[4/3]",
        count === 3 && "h-full",
        count === 3 && index === 0 && "row-span-2",
        count >= 4 && "aspect-square",
      )}
    >
      {item.href && item.kind === "image" ? (
        <a
          href={item.href}
          target="_blank"
          rel="noreferrer"
          className="block h-full w-full"
        >
          {media}
        </a>
      ) : (
        media
      )}
      {overlay}
    </div>
  );
}
